import { useState } from 'react';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `SAR ${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/* Lead capture under the ESG calculator result. Routes through the legacy
   /api/contact fallback pipeline with full breakdown in the message body. */
export const GratuityLeadForm = ({ result, reason, basic, allowances, years, months }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    const lines = [
      'ESG GRATUITY BREAKDOWN REQUEST (Saudi End-of-Service Calculator)',
      '',
      `Termination Reason: ${reason === 'termination' ? 'Employer Termination / Non-Renewal / Force Majeure' : 'Resignation (Article 85 scaling)'}`,
      `Basic Monthly Salary: ${fmt(Number(basic) || 0)}`,
      `Fixed Housing & Transport Allowances: ${fmt(Number(allowances) || 0)}`,
      `Tenure: ${years || 0} years ${months || 0} months (${result.mTotal} months total)`,
      '',
      `Base Salary Considered (Sf): ${fmt(result.sf)}`,
      `Tier 1 Gratuity (first 5 years): ${fmt(result.tier1)}`,
      `Tier 2 Gratuity (beyond 5 years): ${fmt(result.tier2)}`,
      `Applied Settlement Percentage (Ra): ${(result.ra * 100).toFixed(2)}%`,
      `TOTAL ESTIMATED STATUTORY PAYOUT: ${fmt(result.payout)}`,
    ];
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          service_type: 'Saudi ESG Gratuity Calculator',
          country: 'Saudi Arabia',
          qualification: '',
          experience: '',
          message: lines.join('\n'),
          source: 'gratuity-calculator-lead',
        }),
      });
      setStatus(res.ok ? 'done' : 'error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="mt-5 flex items-start gap-3 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg p-5" data-testid="esg-lead-success">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="text-sm text-slate-800 leading-relaxed">
          <p className="font-bold text-emerald-800 mb-1">Breakdown request received!</p>
          <p>Our Saudi payroll specialist will email your personalised gratuity breakdown to <strong>{form.email}</strong> shortly, along with Qiwa/Mudad contract alignment tips.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-5 bg-white border-2 border-[#000080]/15 rounded-xl p-5 sm:p-6" data-testid="esg-lead-form">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-[#000080]" aria-hidden="true" />
        <h3 className="text-base font-bold text-[#000080] font-syne">Email me this gratuity breakdown</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4 leading-snug">Get your personalised Article 84/85 breakdown plus contract-structuring guidance from our Saudi payroll specialists — free.</p>
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <input
          type="text" required maxLength={120} placeholder="Full name" value={form.name} onChange={set('name')}
          data-testid="esg-lead-name" style={{ colorScheme: 'light' }}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#38CAFC]"
        />
        <input
          type="email" required maxLength={120} placeholder="Email address" value={form.email} onChange={set('email')}
          data-testid="esg-lead-email" style={{ colorScheme: 'light' }}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#38CAFC]"
        />
        <input
          type="tel" required maxLength={32} placeholder="Phone / WhatsApp" value={form.phone} onChange={set('phone')}
          data-testid="esg-lead-phone" style={{ colorScheme: 'light' }}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#38CAFC]"
        />
      </div>
      {status === 'error' && (
        <p className="text-xs text-red-600 mb-3" data-testid="esg-lead-error">Something went wrong — please re-check your details and try again, or call +91-9958848998.</p>
      )}
      <button
        type="submit"
        disabled={status === 'sending'}
        data-testid="esg-lead-submit"
        className="inline-flex items-center gap-2 bg-[#000080] hover:bg-[#0056B3] disabled:opacity-60 text-white font-bold text-sm px-6 py-3 rounded-lg transition-colors"
      >
        {status === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Mail className="w-4 h-4" aria-hidden="true" />}
        {status === 'sending' ? 'Sending…' : 'Send My Breakdown'}
      </button>
      <p className="text-[10px] text-slate-400 mt-3">By submitting, you agree to be contacted by Zafco regarding Saudi mobility services. No spam.</p>
    </form>
  );
};
